// @ts-nocheck
import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { XStack, Text, YStack, useTheme } from 'tamagui';
import { Sun, Moon, CaretDown, MagnifyingGlass, Shield, Coins, BookOpen, Rocket, Package, Pulse } from '@phosphor-icons/react';
import { Container } from './Container';
import { useIsMobile } from '../hooks/useIsMobile';

const productItems = [
  { slug: 'torch', name: 'Torch', desc: 'Cross-ecosystem search & intranet', accent: '#d4944a', Icon: MagnifyingGlass },
  { slug: 'statio', name: 'Statio', desc: 'Security for AI agents', accent: '#5a7090', Icon: Shield },
  { slug: 'denarius', name: 'Denarius', desc: 'Financial data & analytics', accent: '#34d399', Icon: Coins },
  { slug: 'janusledger', name: 'JanusLedger', desc: 'Double-entry ledger infra', accent: '#60a5fa', Icon: BookOpen },
];

const ossItems = [
  { slug: 'pilum', name: 'Pilum', desc: 'Multi-cloud deployment CLI', accent: '#fb923c', Icon: Rocket },
  { slug: 'scuta', name: 'Scuta', desc: 'Managed CLI distribution', accent: '#4ade80', Icon: Package },
  { slug: 'capsa', name: 'Capsa', desc: 'Self-hosted documentation platform', accent: '#60a5fa', Icon: BookOpen },
  { slug: 'vigil', name: 'Vigil', desc: 'Network reliability evidence', accent: '#e0a458', Icon: Pulse },
];

export function Header({ theme, toggleTheme }) {
  const isDark = theme === 'dark';
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const tk = useTheme();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const dropdownRef = useRef(null);
  const timeoutRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleDropdownEnter = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setDropdownOpen(true);
  };

  const handleDropdownLeave = () => {
    timeoutRef.current = setTimeout(() => setDropdownOpen(false), 150);
  };

  const navigateToProduct = (slug) => {
    navigate(`/products/${slug}`);
    setDropdownOpen(false);
    setMobileMenuOpen(false);
  };

  return (
    <XStack
      style={{ position: 'sticky', top: 0, zIndex: 200 }}
      backgroundColor="$background"
      borderBottomWidth={1}
      borderBottomColor="$color3"
      paddingVertical="$3"
    >
      <Container>
        <XStack flexDirection="row" justifyContent="space-between" alignItems="center">
          {/* Logo */}
          <XStack
            flexDirection="row" alignItems="baseline" gap="$1"
            cursor="pointer" onPress={() => navigate('/')}
          >
            <Text
              fontSize="$5"
              fontWeight="700"
              fontFamily="Cinzel"
              letterSpacing={1.5}
              style={{
                color: "#a03030",
                fontFamily: "Cinzel",              }}
            >
              ROMANS
            </Text>
            <Text fontSize="$3" fontWeight="400" color="$color7">
              .dev
            </Text>
          </XStack>

          {/* Desktop Nav */}
          {!isMobile && (
            <XStack flexDirection="row" gap="$6" alignItems="center">
              {/* Products dropdown */}
              <div
                ref={dropdownRef}
                onMouseEnter={handleDropdownEnter}
                onMouseLeave={handleDropdownLeave}
                style={{ position: 'relative' }}
              >
                <XStack
                  flexDirection="row"
                  alignItems="center"
                  gap="$1"
                  cursor="pointer"
                  onPress={() => setDropdownOpen(!dropdownOpen)}
                >
                  <Text
                    color="$color8" fontSize="$3" fontWeight="500"
                    hoverStyle={{ color: '$color11' }}
                  >
                    Products
                  </Text>
                  <div style={{
                    display: 'inline-flex',
                    transition: 'transform 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                    transform: dropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                  }}>
                    <CaretDown size={14} color={tk.color7?.get()} />
                  </div>
                </XStack>

                {/* Dropdown panel */}
                {dropdownOpen && (
                  <div
                    onMouseEnter={handleDropdownEnter}
                    onMouseLeave={handleDropdownLeave}
                    style={{
                      position: 'absolute',
                      top: 'calc(100% + 12px)',
                      left: '50%',
                      transform: 'translateX(-50%)',
                      width: 420,
                      borderRadius: 8,
                      border: `1px solid ${isDark ? '#27272a' : '#e4e4e7'}`,
                      backgroundColor: isDark ? '#18181b' : '#f4f4f5',
                      boxShadow: isDark
                        ? '0 16px 48px rgba(0,0,0,0.5)'
                        : '0 16px 48px rgba(0,0,0,0.1)',
                      padding: 16,
                      zIndex: 300,
                      fontFamily: "'Space Grotesk', sans-serif",
                      animation: 'dropdownFadeIn 0.15s ease',
                    }}
                  >
                    <style>{`
                      @keyframes dropdownFadeIn {
                        from { opacity: 0; transform: translateX(-50%) translateY(-4px); }
                        to { opacity: 1; transform: translateX(-50%) translateY(0); }
                      }
                    `}</style>
                    <div style={{ marginBottom: 12 }}>
                      <Text fontSize={11} color="$color6" letterSpacing={2} fontWeight="600" marginBottom="$2">
                        PRODUCTS
                      </Text>
                      <YStack gap="$1" marginTop={8}>
                        {productItems.map((item) => (
                          <XStack
                            key={item.slug}
                            flexDirection="row" alignItems="center" gap="$3"
                            paddingVertical="$2" paddingHorizontal="$2"
                            borderRadius="$3" cursor="pointer"
                            hoverStyle={{ backgroundColor: '$color3' }}
                            onPress={() => navigateToProduct(item.slug)}
                          >
                            <YStack
                              width={32} height={32} borderRadius="$2"
                              alignItems="center" justifyContent="center"
                              style={{ backgroundColor: `${item.accent}15` }}
                            >
                              <item.Icon size={16} color={item.accent} />
                            </YStack>
                            <YStack flex={1}>
                              <Text fontSize="$3" fontWeight="600" color="$color12">{item.name}</Text>
                              <Text fontSize="$2" color="$color7">{item.desc}</Text>
                            </YStack>
                          </XStack>
                        ))}
                      </YStack>
                    </div>

                    <YStack height={1} backgroundColor="$color4" marginVertical="$2" />

                    <div>
                      <Text fontSize={11} color="$color6" letterSpacing={2} fontWeight="600" marginBottom="$2">
                        OPEN SOURCE
                      </Text>
                      <YStack gap="$1" marginTop={8}>
                        {ossItems.map((item) => (
                          <XStack
                            key={item.slug}
                            flexDirection="row" alignItems="center" gap="$3"
                            paddingVertical="$2" paddingHorizontal="$2"
                            borderRadius="$3" cursor="pointer"
                            hoverStyle={{ backgroundColor: '$color3' }}
                            onPress={() => navigateToProduct(item.slug)}
                          >
                            <YStack
                              width={32} height={32} borderRadius="$2"
                              alignItems="center" justifyContent="center"
                              style={{ backgroundColor: `${item.accent}15` }}
                            >
                              <item.Icon size={16} color={item.accent} />
                            </YStack>
                            <YStack flex={1}>
                              <Text fontSize="$3" fontWeight="600" color="$color12">{item.name}</Text>
                              <Text fontSize="$2" color="$color7">{item.desc}</Text>
                            </YStack>
                          </XStack>
                        ))}
                      </YStack>
                    </div>
                  </div>
                )}
              </div>

              <Text
                color="$color8" fontSize="$3" fontWeight="500"
                hoverStyle={{ color: '$color11' }} cursor="pointer"
                onPress={() => navigate('/about')}
              >
                About
              </Text>
              <Text
                color="$color8" fontSize="$3" fontWeight="500"
                hoverStyle={{ color: '$color11' }} cursor="pointer"
                onPress={() => window.open('https://logicalbytes.dev', '_blank')}
              >
                Blog
              </Text>
              <YStack
                cursor="pointer"
                onPress={toggleTheme}
                hoverStyle={{ opacity: 0.7 }}
                padding="$1"
              >
                {isDark
                  ? <Sun size={16} color={tk.amber7?.get()} />
                  : <Moon size={16} color={tk.color8?.get()} />
                }
              </YStack>
            </XStack>
          )}

          {/* Mobile Nav */}
          {isMobile && (
            <XStack flexDirection="row" gap="$3" alignItems="center">
              <YStack cursor="pointer" onPress={toggleTheme} padding="$1">
                {isDark ? <Sun size={16} color={tk.amber7?.get()} /> : <Moon size={16} color={tk.color8?.get()} />}
              </YStack>
              <YStack cursor="pointer" onPress={() => setMobileMenuOpen(!mobileMenuOpen)} padding="$1">
                <Text fontSize="$5" color="$color9">
                  {mobileMenuOpen ? '\u2715' : '\u2630'}
                </Text>
              </YStack>
            </XStack>
          )}
        </XStack>

        {/* Mobile menu overlay */}
        {isMobile && mobileMenuOpen && (
          <YStack
            position="absolute" top={56} left={0} right={0}
            backgroundColor={isDark ? '#18181b' : '#f4f4f5'}
            borderBottomWidth={1} borderBottomColor="$color4"
            padding="$4" gap="$3"
            style={{ boxShadow: isDark ? '0 16px 48px rgba(0,0,0,0.5)' : '0 16px 48px rgba(0,0,0,0.1)' }}
          >
            <Text fontSize={11} color="$color6" letterSpacing={2} fontWeight="600">PRODUCTS</Text>
            {productItems.map((item) => (
              <Text key={item.slug} fontSize="$4" color="$color10" fontWeight="500" cursor="pointer" paddingVertical="$1" onPress={() => navigateToProduct(item.slug)}>
                {item.name}
              </Text>
            ))}
            <YStack height={1} backgroundColor="$color4" />
            <Text fontSize={11} color="$color6" letterSpacing={2} fontWeight="600">OPEN SOURCE</Text>
            {ossItems.map((item) => (
              <Text key={item.slug} fontSize="$4" color="$color10" fontWeight="500" cursor="pointer" paddingVertical="$1" onPress={() => navigateToProduct(item.slug)}>
                {item.name}
              </Text>
            ))}
            <YStack height={1} backgroundColor="$color4" />
            <Text fontSize="$4" color="$color10" fontWeight="500" cursor="pointer" paddingVertical="$1" onPress={() => { navigate('/about'); setMobileMenuOpen(false); }}>
              About
            </Text>
            <Text fontSize="$4" color="$color10" fontWeight="500" cursor="pointer" paddingVertical="$1" onPress={() => { window.open('https://logicalbytes.dev', '_blank'); setMobileMenuOpen(false); }}>
              Blog
            </Text>
          </YStack>
        )}
      </Container>
    </XStack>
  );
}
